import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { TreeCollapse, Tree, IconButton } from '@picsio/ui';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  any, arrayOf, func, objectOf, string,
} from 'prop-types';
import { Web } from '@picsio/icons';
import { LocalStorage } from '../../shared/utils';

import localization from '../../shared/strings';
import { setWebsitesTreePromoBlockHidden } from '../../store/reducers/promoBlockSlice';
import { getActivePlansSelector } from '../../store/selectors/billing';
import { navigate } from '../../helpers/history';
import { selectAdminRoles } from '../../store/selectors/teammates';
import { isHaveTeammatePermission } from '../../store/helpers/user';
import Logger from '../../services/Logger';
import { showDialog } from '../dialog';
import PromotionalBlock from '../PromotionalBlock';
import Tag from '../Tag/Tag';

const KEY_IN_LOCAL_STORAGE = 'picsio.tagsTree.websites.open';

let tempExpanded = [];

export default function WebsitesTree({
  renderNode, selected = [], onNodeSelect = () => {}, titleProps = {},
}) {
  const isLoaded = useSelector((state) => state.collections.isLoaded);
  const websites = useSelector((state) => state.collections.search?.collections?.websites || state.collections.collections?.websites);
  const { isWebsitesTreePromoBlockHidden } = useSelector((state) => state.promoBlock);
  const subscriptionFeatures = useSelector((state) => state.user.subscriptionFeatures);
  const user = useSelector((state) => state.user);
  const activePlan = useSelector(getActivePlansSelector);
  const adminRoles = useSelector(selectAdminRoles);

  const websitesLimit = get(activePlan, 'plan.metadata.websitesLimit', 0);
  const usedWebsitesCount = get(subscriptionFeatures, 'websitesCount', 0);
  const isWebSitesPanelClosed = get(user, 'team.settings.isWebSitesPanelClosed');

  const [expanded, setExpanded] = useState(tempExpanded);
  const dispatch = useDispatch();

  const saved = LocalStorage.get(KEY_IN_LOCAL_STORAGE);

  const defaultPanelState = (isWebSitesPanelClosed !== undefined ? !isWebSitesPanelClosed : true);
  const initialPanelState = saved !== null ? saved : defaultPanelState;

  const [isOpen, setOpen] = useState(initialPanelState);

  useEffect(() => {
    if (!isLoaded) setExpanded([]);
  }, [isLoaded]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      LocalStorage.set(KEY_IN_LOCAL_STORAGE, !prev);
      return !prev;
    });
  }, [setOpen]);

  useEffect(() => {
    tempExpanded = [...expanded];
  }, [expanded]);

  const handleToggleChild = useCallback((collectionId) => {
    if (expanded.includes(collectionId)) setExpanded((prev) => prev.filter((id) => id !== collectionId));
    else setExpanded((prev) => [...prev, collectionId]);
  }, [expanded, setExpanded]);

  const isPromoBlockVisible = !isWebsitesTreePromoBlockHidden && subscriptionFeatures?.obsolete && !subscriptionFeatures?.hideUpdateComparison;

  const onClosePromotionalBlock = useCallback(() => {
    Logger.log('User', 'PromotionalBannerClosed', { banner_location: 'Left tree panel', banner_type: 'Unlimited Websites' });

    dispatch(setWebsitesTreePromoBlockHidden(true));
  }, [dispatch]);

  const adminRolesEmails = useMemo(() => (adminRoles.slice(0, 5).map((role) => (
    <>
      <Tag
        key={role._id}
        type="user"
        avatar={role.avatar}
        text={role.email}
      />
    </>
  ))), [adminRoles]);

  const onClickPromotionalBlock = useCallback(() => {
    Logger.log('User', 'PromotionalBannerClicked', { banner_location: 'Left tree panel', banner_type: 'Unlimited Websites' });

    if (isHaveTeammatePermission('manageBilling', user)) {
      navigate('/billing?tab=overview');

      return;
    }

    showDialog({
      title: localization.PROMOTIONAL_BLOCK.title,
      text: localization.PROMOTIONAL_BLOCK.text(adminRolesEmails),
      textBtnOk: localization.DIALOGS.LOGOUT_DIALOG.OK_TEXT,
      textBtnCancel: null,
    });
  }, [adminRolesEmails, user]);

  /** if no websites */
  if (!websites?.nodes?.length) return null;

  /** className added for QA */
  return (
    <TreeCollapse
      className="collectionsWebsites"
      title={websites.name || 'Loading...'}
      isOpen={isOpen}
      {...titleProps}
      selected={selected.includes(websites._id)}
      onClick={toggleOpen}
    >
      <If condition={isPromoBlockVisible}>
        <PromotionalBlock
          icon={(
            <IconButton
              size="lg"
              className="searchFilterIcon"
              color="primary"
            >
              <Web />
            </IconButton>
          )}
          title={localization.PROMOTIONAL_BANNER.COLLECTION_TREE.title}
          description={localization.PROMOTIONAL_BANNER.COLLECTION_TREE.description(usedWebsitesCount, websitesLimit)}
          onClickCallback={onClickPromotionalBlock}
          onCloseCallback={onClosePromotionalBlock}
        />
      </If>

      <Tree
        nodes={websites?.nodes || []}
        renderNode={renderNode}
        onNodeToggle={handleToggleChild}
        onNodeSelect={onNodeSelect}
        controlled
        expanded={expanded}
        selected={selected}
      />
    </TreeCollapse>

  );
}

WebsitesTree.propTypes = {
  renderNode: func.isRequired,
  titleProps: objectOf(any),
  selected: arrayOf(string),
  onNodeSelect: func,
};
